import { defineStore } from 'pinia'
import { unref } from 'vue'

import { auth } from '@/api'
import Route from '@/constants/route'
import Store from '@/constants/store'
import router from '@/router'
import * as models from '@/store/model'
import { useCartStore } from '@/store/cart'
import { useShopStore } from '@/store/shop'
import { useRoute } from 'vue-router'

export const useAuthStore = defineStore(Store.AUTH, {
    state: () => ({
        user: null,
        return: null,
        impersonating: false // Be careful with this one - won't show the correct value if the page is refreshed
    }),

    getters: {
        isAuthenticated: (state) => state.user != null
    },

    actions: {
        async login (email, password, remember) {
            const { data } = await auth.login(unref(email), unref(password), unref(remember))

            this.setUser(data)
        },

        async logout (redirect = true) {
            const response = await auth.logout()

            if (response.data) {    
                const name = this.return?.name || Route.HOME
                const params = this.return?.params || {}                
                
                this.setUser(response.data)
                this.return = null
                this.impersonating = false

                if (redirect) {
                    router.push({ name, params })
                }

                return
            }

            this.setUser(null)

            if (redirect) {
                router.push({ name: Route.LOGIN })
            }
        },

        async register (payload) {
            const { data } = await auth.register(unref(payload))

            this.setUser(data)
        },

        async refresh () {
            try {
                const { data } = await auth.check()

                this.setUser(data)
            } catch (error) {
                this.setUser(null)
            }

            return this.isAuthenticated
        },

        async check () {
            //console.log(router.current)
            try {
                return await this.refresh()
            } catch (e) {
            }

            return this.isAuthenticated
        },

        async impersonate(id, route) {
            const { data } = await auth.impersonate(id)
    
            this.setUser(data)
            this.return = { name: unref(route.name), params: unref(route.params) }
            this.impersonating = true
        },

        /**
         * Set the authenticated user, clearing the previous user's data when it changes.
         *
         * The stores outlive the user: without this, a parent being impersonated would
         * inherit the admin's table filters (an empty children list) and the shop state
         * of the previous parent. A change detected by a check (the session expired
         * during an impersonation and the remember cookie logged the admin back in)
         * also ends the impersonation on this side.
         *
         * @param {Object|null} user
         * @return {void}
         */
        setUser (user) {
            const changed = (this.user?.id ?? null) !== (user?.id ?? null)

            this.user = user

            if (!changed) {
                return
            }

            this.return = null
            this.impersonating = false

            Object.values(models).forEach(useStore => useStore().$reset())
            useShopStore().$reset()
            useCartStore().$reset()
        }
    }
})
